import { type ReactNode, useMemo, useState } from 'react';

import { BulkAskAvailabilityBar } from '@apps/web/components/caregivers/bulk_ask_availability_bar';
import { CaregiverDetailModal } from '@apps/web/components/caregivers/caregiver_detail_modal';
import { Badge } from '@apps/web/components/ui/badge';
import { Checkbox } from '@apps/web/components/ui/checkbox';
import { DataTable } from '@apps/web/components/ui/data_table';
import {
    type CoverageRow,
    EMPLOYMENT_TYPE_LABELS,
    type WorkWeekStartDay,
} from '@apps/web/features/coverage/model/coverage_types';
import { AvailabilityDetailDialog } from '@apps/web/features/coverage/ui/availability_detail_dialog';
import { AvailabilityStrip } from '@apps/web/features/coverage/ui/availability_strip';
import { useBulkAskAvailability } from '@apps/web/hooks/use_bulk_ask_availability';
import { comparePersonNames } from '@apps/web/lib/caregiver_name';
import { type ColumnDef } from '@apps/web/lib/table';
import { useOrganization } from '@apps/web/swr_data_fetching/data_fetching_hooks';

const MAX_VISIBLE_TAGS = 3;

function buildAskAvailabilityMessage(names: ReadonlyArray<string>): string {
    return `Text these caregivers and ask about their availability: ${names.join(', ')}.`;
}

export function CoverageGridPane({
    rows,
    workWeekStartDay,
    isLoading,
    organizationId,
    renderEmptyState,
}: {
    readonly rows: Array<CoverageRow>;
    readonly workWeekStartDay: WorkWeekStartDay;
    readonly isLoading: boolean;
    readonly organizationId: string | null;
    readonly renderEmptyState?: () => ReactNode;
}) {
    const [detailRow, setDetailRow] = useState<CoverageRow | null>(null);
    const [selectedCaregiverId, setSelectedCaregiverId] = useState<
        string | null
    >(null);
    const bulkAskAvailability = useBulkAskAvailability(
        'coverage_bulk_ask_availability',
    );
    const { data: organization } = useOrganization(organizationId, {
        reason: 'coverage-grid-pane',
    });
    const sortByLastName =
        organization?.features?.last_name_sorting_enabled === true;

    const columns = useMemo<Array<ColumnDef<CoverageRow, unknown>>>(
        () => [
            {
                id: 'select',
                size: 36,
                enableSorting: false,
                enableHiding: false,
                header: ({ table }) => {
                    const pageRows = table.getRowModel().rows;
                    const pageSelectedCount = pageRows.filter((row) =>
                        bulkAskAvailability.isSelected(
                            row.original.caregiver_id,
                        ),
                    ).length;
                    const allOnPageSelected =
                        pageRows.length > 0 &&
                        pageSelectedCount === pageRows.length;
                    const checkboxState: boolean | 'indeterminate' =
                        allOnPageSelected
                            ? true
                            : pageSelectedCount > 0
                              ? 'indeterminate'
                              : false;
                    return (
                        <Checkbox
                            checked={checkboxState}
                            onCheckedChange={() => {
                                pageRows.forEach((row) => {
                                    const alreadySelected =
                                        bulkAskAvailability.isSelected(
                                            row.original.caregiver_id,
                                        );
                                    if (allOnPageSelected === alreadySelected) {
                                        bulkAskAvailability.toggle(
                                            row.original.caregiver_id,
                                            row.original.name,
                                        );
                                    }
                                });
                            }}
                            aria-label="Select all caregivers on this page"
                        />
                    );
                },
                cell: ({ row }) => (
                    <Checkbox
                        checked={bulkAskAvailability.isSelected(
                            row.original.caregiver_id,
                        )}
                        onCheckedChange={() => {
                            bulkAskAvailability.toggle(
                                row.original.caregiver_id,
                                row.original.name,
                            );
                        }}
                        onClick={(event) => {
                            event.stopPropagation();
                        }}
                        aria-label={`Select ${row.original.name}`}
                    />
                ),
            },
            {
                id: 'name',
                header: 'Caregiver',
                accessorFn: (row) => row.name,
                enableSorting: true,
                sortFn: (rowA, rowB) =>
                    comparePersonNames(
                        rowA.original.name,
                        rowB.original.name,
                        sortByLastName,
                    ),
                cell: ({ row }) => (
                    <div>
                        <button
                            type="button"
                            onClick={() =>
                                setSelectedCaregiverId(
                                    row.original.caregiver_id,
                                )
                            }
                            className="font-medium whitespace-nowrap hover:underline"
                        >
                            {row.original.name}
                        </button>
                        <div className="flex gap-1">
                            {!row.original.is_active ? (
                                <span className="text-muted-foreground bg-muted mt-0.5 inline-block rounded-full px-2 text-[10px] font-medium">
                                    Inactive
                                </span>
                            ) : null}
                            {!row.original.has_availability_data ? (
                                <span className="text-muted-foreground border-muted-foreground/50 mt-0.5 inline-block rounded-full border border-dashed px-2 text-[10px] font-medium">
                                    No availability data
                                </span>
                            ) : null}
                        </div>
                    </div>
                ),
            },
            {
                id: 'location',
                header: 'Location',
                accessorFn: (row) => row.city ?? '',
                enableSorting: true,
                cell: ({ row }) => (
                    <div className="whitespace-nowrap">
                        <div>
                            {row.original.city ?? '—'}{' '}
                            <span className="text-muted-foreground tabular-nums">
                                {row.original.postal_code ?? ''}
                            </span>
                        </div>
                        {row.original.office_name ? (
                            <div className="text-muted-foreground text-xs">
                                {row.original.office_name}
                            </div>
                        ) : null}
                    </div>
                ),
            },
            {
                id: 'availability',
                header: 'Availability',
                enableSorting: false,
                cell: ({ row }) => (
                    <AvailabilityStrip
                        row={row.original}
                        workWeekStartDay={workWeekStartDay}
                        onOpenDetail={setDetailRow}
                    />
                ),
            },
            {
                id: 'tags',
                header: 'Tags',
                enableSorting: false,
                cell: ({ row }) => {
                    const tags = row.original.tags;
                    const visible = tags.slice(0, MAX_VISIBLE_TAGS);
                    const hiddenCount = tags.length - visible.length;
                    return (
                        <div className="flex max-w-52 flex-wrap gap-1">
                            {visible.map((tag) => (
                                <Badge key={tag} variant="outline">
                                    {tag}
                                </Badge>
                            ))}
                            {hiddenCount > 0 ? (
                                <Badge
                                    variant="outline"
                                    title={tags
                                        .slice(MAX_VISIBLE_TAGS)
                                        .join(', ')}
                                >
                                    +{hiddenCount}
                                </Badge>
                            ) : null}
                        </div>
                    );
                },
            },
            {
                id: 'employment',
                header: 'Employment',
                accessorFn: (row) => row.employment_type ?? '',
                enableSorting: true,
                cell: ({ row }) => (
                    <span className="text-muted-foreground whitespace-nowrap">
                        {row.original.employment_type
                            ? (EMPLOYMENT_TYPE_LABELS[
                                  row.original.employment_type
                              ] ?? row.original.employment_type)
                            : '—'}
                    </span>
                ),
            },
        ],
        [workWeekStartDay, bulkAskAvailability, sortByLastName],
    );

    return (
        <div className="space-y-3">
            <BulkAskAvailabilityBar
                selectedCount={bulkAskAvailability.selectedCount}
                batchCount={bulkAskAvailability.batchCount}
                nextBatchNumber={bulkAskAvailability.nextBatchNumber}
                allBatchesAsked={bulkAskAvailability.allBatchesAsked}
                onAsk={() =>
                    bulkAskAvailability.askNextBatch(
                        buildAskAvailabilityMessage,
                    )
                }
                onClear={bulkAskAvailability.clear}
            />
            <DataTable
                columns={columns}
                data={rows}
                isLoading={isLoading}
                initialSorting={[{ id: 'name', desc: false }]}
                pageSize={50}
                renderEmptyState={renderEmptyState}
            />
            <AvailabilityDetailDialog
                row={detailRow}
                workWeekStartDay={workWeekStartDay}
                onClose={() => setDetailRow(null)}
            />
            {organizationId ? (
                <CaregiverDetailModal
                    isOpen={selectedCaregiverId !== null}
                    onClose={() => setSelectedCaregiverId(null)}
                    organizationId={organizationId}
                    caregiverId={selectedCaregiverId ?? undefined}
                />
            ) : null}
        </div>
    );
}
